import { useEffect, useRef } from 'react';
import maplibregl from 'maplibre-gl';
import { colors } from '../../styles/theme';
import { whenMapStyleReady } from '../../Map/hooks/useListPinsLayer';
import {
  CHIP_SIZE, CHIP_TAIL, CLUSTER_MAX_ZOOM, CLUSTER_SIZE, TOOLTIP_GAP, chipTooltipOffset, layoutRecordChips,
} from '../../Map/utils/sharedRecordChips';
import { clampMapFitPadding, prepareMapForFit } from '../../Map/utils/fitMapBoundsToLots';
import {
  buildComparableMapPoints,
  buildComparableTooltipHtml,
  buildClusterTooltipHtml,
  buildMarketApproxGeoJSON,
  buildMarketRadiusGeoJSON,
  circleBounds,
  marketChipWidth,
  normalizeMarketCenter,
  pickComparableTooltipAnchor,
} from '../marketComparablesMap';
import '../../Map/Map.css';

/** Ids de sources / capas del modo Comparables (todas con este prefijo). */
export const MARKET_LAYER_IDS = Object.freeze({
  radiusSource: 'market-cmp-radius',
  radiusFill: 'market-cmp-radius-fill',
  radiusLine: 'market-cmp-radius-line',
  approxSource: 'market-cmp-approx',
  approxFill: 'market-cmp-approx-fill',
  approxLine: 'market-cmp-approx-line',
});

/**
 * Offset del popup para cada ancla, relativo al punto del marker (el chip se ancla abajo con
 * `entry.offset`). Arriba usa `chipTooltipOffset` (encima de todo el abanico).
 * @param {{ offset?: [number, number], stackTop?: number, tail?: boolean }} entry
 * @param {number} [chipWidth]
 * @returns {{ bottom: [number, number], top: [number, number], left: [number, number], right: [number, number] }}
 */
export function comparableTooltipOffsets(entry, chipWidth = CHIP_SIZE.width) {
  const dx = Math.round(entry?.offset?.[0] ?? 0);
  const dy = Math.round(entry?.offset?.[1] ?? 0);
  const faceBottom = dy - (entry?.tail ? CHIP_TAIL : 0);
  const midY = Math.round(faceBottom - CHIP_SIZE.height / 2);
  const half = Math.round(chipWidth / 2);
  return {
    bottom: chipTooltipOffset(entry),
    top: [dx, dy + TOOLTIP_GAP],
    left: [dx + half + TOOLTIP_GAP, midY],
    right: [dx - half - TOOLTIP_GAP, midY],
  };
}

/** Offset del popup del clúster (círculo de 36 px anclado al centro). */
export function clusterTooltipOffsets() {
  const d = CLUSTER_SIZE / 2 + TOOLTIP_GAP;
  return { bottom: [0, -d], top: [0, d], left: [d, 0], right: [-d, 0] };
}

/** Tamaño del tooltip si todavía no se puede medir (con foto: 240 × ~190 px). */
const POPUP_FALLBACK_SIZE = Object.freeze({ width: 240, height: 190 });

const TEAL = '#489291';
const TEAL_LIGHT = '#7CC5C4';

/**
 * Orden de los markers DOM (chips y unidad son `maplibregl.Marker`, no capas: el orden lo da
 * el `z-index` del elemento dentro del contenedor del mapa). Chips 1–3 (hover arriba de
 * seleccionado, seleccionado arriba del resto), «Tu unidad» 5: siempre arriba de los chips.
 * El tooltip (`.terrio-record-chip-tooltip`, z-index 8 en Map.css) queda arriba de todo.
 */
export const MARKET_Z = Object.freeze({ chip: 1, selected: 2, hovered: 3, unit: 5 });

/** ¿El puntero puede hacer hover? En touch el tooltip sale solo con el toque (selección). */
const canHover = () => {
  try {
    return typeof window.matchMedia !== 'function' || window.matchMedia('(hover: hover)').matches;
  } catch (_) {
    return true;
  }
};

/** Saca capas y sources del modo (tolerante a estilo a medias o mapa destruido). */
export function removeMarketComparablesLayers(map) {
  if (!map) return;
  const ids = MARKET_LAYER_IDS;
  [ids.approxLine, ids.approxFill, ids.radiusLine, ids.radiusFill].forEach((id) => {
    try { if (map.getLayer(id)) map.removeLayer(id); } catch (_) { /* estilo a medias */ }
  });
  [ids.approxSource, ids.radiusSource].forEach((id) => {
    try { if (map.getSource(id)) map.removeSource(id); } catch (_) { /* estilo a medias */ }
  });
}

const upsertSource = (map, id, data) => {
  const src = map.getSource(id);
  if (src && typeof src.setData === 'function') {
    src.setData(data);
    return;
  }
  map.addSource(id, { type: 'geojson', data });
};

/**
 * Crea o actualiza el círculo del radio y las zonas punteadas (~100 m) de los aproximados.
 * Se agregan arriba del stack (encima de parcelas y edificios); chips y unidad son markers
 * HTML y quedan sobre el canvas.
 */
export function upsertMarketComparablesLayers(map, { radius, approx }) {
  if (!map) return;
  const ids = MARKET_LAYER_IDS;
  upsertSource(map, ids.radiusSource, radius);
  upsertSource(map, ids.approxSource, approx);
  if (!map.getLayer(ids.radiusFill)) {
    map.addLayer({
      id: ids.radiusFill,
      type: 'fill',
      source: ids.radiusSource,
      paint: { 'fill-color': TEAL, 'fill-opacity': 0.07 },
    });
  }
  if (!map.getLayer(ids.radiusLine)) {
    map.addLayer({
      id: ids.radiusLine,
      type: 'line',
      source: ids.radiusSource,
      paint: { 'line-color': TEAL_LIGHT, 'line-width': 1.5, 'line-opacity': 0.8, 'line-dasharray': [2, 2] },
    });
  }
  if (!map.getLayer(ids.approxFill)) {
    map.addLayer({
      id: ids.approxFill,
      type: 'fill',
      source: ids.approxSource,
      paint: { 'fill-color': TEAL_LIGHT, 'fill-opacity': 0.1 },
    });
  }
  if (!map.getLayer(ids.approxLine)) {
    map.addLayer({
      id: ids.approxLine,
      type: 'line',
      source: ids.approxSource,
      paint: { 'line-color': TEAL_LIGHT, 'line-width': 1, 'line-opacity': 0.9, 'line-dasharray': [1.5, 1.5] },
    });
  }
}

/** Marcador de la unidad: el mismo punto que el informe compartido + «Tu unidad · valor». */
export function createMarketUnitMarkerElement(label = '') {
  const el = document.createElement('div');
  el.setAttribute('data-testid', 'market-unit-marker');
  el.setAttribute('aria-label', 'Tu unidad');
  Object.assign(el.style, {
    width: '18px',
    height: '18px',
    borderRadius: '50%',
    background: colors.primary,
    border: '3px solid #FFFFFF',
    boxShadow: '0 0 0 6px rgba(0,0,0,0.18), 0 2px 8px rgba(0,0,0,0.45)',
    boxSizing: 'border-box',
    // Sin eventos: un chip tapado en parte por el rótulo sigue respondiendo al hover y al clic.
    pointerEvents: 'none',
    zIndex: String(MARKET_Z.unit),
  });
  if (label) {
    const tag = document.createElement('span');
    tag.className = 'terrio-market-unit-label';
    tag.textContent = label;
    el.appendChild(tag);
  }
  return el;
}

/**
 * Modo Comparables en el mapa principal: círculo del radio, zonas aproximadas, marcador de la
 * unidad y chips de precio/m² (markers HTML con clúster, layout de `layoutRecordChips`).
 * Hover de chip ↔ fila (`hoveredKey` / `onHoverKey`). Tooltip con foto: al pasar el mouse
 * por un chip del mapa (se cierra al salir; si hay seleccionado, vuelve el suyo) y al hacer
 * clic, que además fija la selección (`selectedKey` / `onSelectKey`). Hover de un clúster:
 * lista corta de precios de adentro (sin foto); el clic sigue acercando. Un solo tooltip a la
 * vez; en touch solo con el toque (clúster: el toque acerca, sin tooltip). El hover desde la
 * fila resalta el chip y abre el mismo tooltip si el chip está suelto en pantalla (en clúster
 * o fuera de la vista solo resalta). Las capas se vuelven a poner tras cada recarga
 * de estilo (`whenMapStyleReady` + `styledata`). Con `enabled` en false se borra todo.
 *
 * @param {object} options
 * @param {React.MutableRefObject} options.mapInstanceRef
 * @param {boolean} options.enabled
 * @param {{lat:number,lng:number}|null} options.center
 * @param {number|null} options.radiusM - radio final de la búsqueda (`search.radius_m`)
 * @param {object[]} options.comparables - comparables normalizados del bloque
 * @param {object} [options.block] - bloque (moneda / operación para los textos)
 * @param {string} [options.unitLabel] - texto junto al marcador («Tu unidad · 2.000»)
 * @param {string|null} [options.hoveredKey]
 * @param {string|null} [options.selectedKey]
 * @param {(key: string|null) => void} [options.onHoverKey]
 * @param {(key: string|null) => void} [options.onSelectKey]
 * @param {object|number} [options.fitPadding] - deja libre el Dock
 * @param {string} [options.fitKey] - cambia → vuelve a encuadrar el círculo
 */
export function useMarketComparablesLayer({
  mapInstanceRef,
  enabled,
  center,
  radiusM,
  comparables,
  block = null,
  unitLabel = '',
  hoveredKey = null,
  selectedKey = null,
  onHoverKey,
  onSelectKey,
  fitPadding = 70,
  fitKey = '',
}) {
  const markersRef = useRef(new Map());
  const unitMarkerRef = useRef(null);
  const popupRef = useRef(null);
  const popupKeyRef = useRef(null);
  /** Chip del mapa bajo el mouse (no el hover que viene de la lista). */
  const mapHoverKeyRef = useRef(null);
  /** Clúster del mapa bajo el mouse (`entry.key`, p. ej. `cluster:…`). */
  const mapHoverClusterKeyRef = useRef(null);
  const callbacksRef = useRef({});
  callbacksRef.current = { onHoverKey, onSelectKey };
  const stateRef = useRef({ hoveredKey, selectedKey, block, comparables });
  stateRef.current = { hoveredKey, selectedKey, block, comparables };
  const fitPaddingRef = useRef(fitPadding);
  fitPaddingRef.current = fitPadding;

  const safeCenter = normalizeMarketCenter(center);
  const centerKey = safeCenter ? `${safeCenter.lat},${safeCenter.lng}` : '';
  const active = Boolean(enabled && safeCenter);

  const hidePopup = () => {
    popupKeyRef.current = null;
    if (popupRef.current) {
      try { popupRef.current.remove(); } catch (_) { /* mapa destruido */ }
      popupRef.current = null;
    }
  };

  /**
   * Ancla según el espacio libre (header de la app / del mapa y Dock = padding de cámara de la
   * ficha). Se mide el popup ya puesto y se corrige antes del próximo frame: sin parpadeo.
   * MapLibre elige solo el ancla si no se fija, pero mira el borde del contenedor, que queda
   * debajo de los headers; por eso se calcula acá.
   */
  const offsetsFor = (entry) => (
    entry?.type === 'cluster'
      ? clusterTooltipOffsets()
      : comparableTooltipOffsets(entry, marketChipWidth(stateRef.current.block?.operation))
  );

  const placePopup = (map, popup, entry) => {
    const offsets = offsetsFor(entry);
    let anchor = popup.options.anchor || 'bottom';
    try {
      const container = map.getContainer();
      const rect = popup.getElement()?.getBoundingClientRect();
      const size = rect && rect.width > 0 && rect.height > 0
        ? { width: rect.width, height: rect.height }
        : (entry?.type === 'cluster'
          ? { width: 200, height: 16 + 24 * Math.min(entry.count || 2, 8) }
          : POPUP_FALLBACK_SIZE);
      const pad = clampMapFitPadding(map, fitPaddingRef.current);
      anchor = pickComparableTooltipAnchor({
        point: map.project(entry.lngLat),
        size,
        view: { width: container.clientWidth, height: container.clientHeight },
        padding: typeof pad === 'number' ? { top: pad, right: pad, bottom: pad, left: pad } : pad,
        offsets,
        current: popupKeyRef.current === entry.key ? popup.options.anchor : null,
      });
    } catch (_) { /* mapa destruido: queda arriba */ }
    if (anchor !== popup.options.anchor) {
      popup.options.anchor = anchor;
      popup.setOffset(offsets[anchor]);
    }
  };

  const showPopup = (map, entry) => {
    if (!map || !entry) return;
    const isCluster = entry.type === 'cluster';
    if (!isCluster && entry.type !== 'chip') return;
    if (popupKeyRef.current === entry.key && popupRef.current) {
      placePopup(map, popupRef.current, entry);
      return;
    }
    hidePopup();
    const html = isCluster
      ? buildClusterTooltipHtml(stateRef.current.comparables, entry.recordKeys, stateRef.current.block || {})
      : buildComparableTooltipHtml(entry.record.comparable, stateRef.current.block || {});
    if (!html) return;
    const offsets = offsetsFor(entry);
    const popup = new maplibregl.Popup({
      closeButton: false,
      closeOnClick: false,
      anchor: 'bottom',
      offset: offsets.bottom,
      maxWidth: isCluster ? '260px' : '240px',
      className: 'terrio-parcel-tooltip terrio-cad-share-tooltip terrio-record-chip-tooltip',
    })
      .setLngLat(entry.lngLat)
      .setHTML(html)
      .addTo(map);
    popupRef.current = popup;
    placePopup(map, popup, entry);
    popupKeyRef.current = entry.key;
  };

  /**
   * Tooltip vigente: clúster bajo el mouse; si no, el comparable en hover (chip del mapa o
   * fila) si su chip está suelto; si no, el seleccionado. En touch el hover no abre tooltip.
   * Nunca mueve el mapa.
   */
  const syncPopup = (map) => {
    const markers = markersRef.current;
    const asChip = (key) => {
      const slot = key ? markers.get(key) : null;
      return slot && slot.entry.type === 'chip' ? slot : null;
    };
    const asCluster = (key) => {
      const slot = key ? markers.get(key) : null;
      return slot && slot.entry.type === 'cluster' ? slot : null;
    };
    if (mapHoverKeyRef.current && !asChip(mapHoverKeyRef.current)) mapHoverKeyRef.current = null;
    if (mapHoverClusterKeyRef.current && !asCluster(mapHoverClusterKeyRef.current)) {
      mapHoverClusterKeyRef.current = null;
    }
    const clusterSlot = canHover() ? asCluster(mapHoverClusterKeyRef.current) : null;
    if (map && clusterSlot) {
      showPopup(map, clusterSlot.entry);
      return;
    }
    const hoverKey = mapHoverKeyRef.current || (canHover() ? stateRef.current.hoveredKey : null);
    const slot = asChip(hoverKey) || asChip(stateRef.current.selectedKey);
    if (map && slot) showPopup(map, slot.entry);
    else hidePopup();
  };

  const applyState = (slot) => {
    const { el, entry } = slot;
    const { hoveredKey: hov, selectedKey: sel } = stateRef.current;
    const keys = entry.type === 'cluster' ? entry.recordKeys : [entry.key];
    const isSelected = sel != null && keys.includes(sel);
    const isHovered = (entry.type === 'cluster' && mapHoverClusterKeyRef.current === entry.key)
      || (hov != null && keys.includes(hov));
    el.classList.toggle('is-selected', isSelected);
    el.classList.toggle('is-hovered', isHovered);
    el.style.zIndex = String(isHovered ? MARKET_Z.hovered : (isSelected ? MARKET_Z.selected : MARKET_Z.chip));
  };

  const removeAll = () => {
    mapHoverKeyRef.current = null;
    mapHoverClusterKeyRef.current = null;
    hidePopup();
    markersRef.current.forEach(({ marker }) => {
      try { marker.remove(); } catch (_) { /* mapa destruido */ }
    });
    markersRef.current.clear();
    if (unitMarkerRef.current) {
      try { unitMarkerRef.current.remove(); } catch (_) { /* mapa destruido */ }
      unitMarkerRef.current = null;
    }
  };

  // Capas GeoJSON (círculo + aproximados): sobreviven a recargas de estilo.
  useEffect(() => {
    const map = mapInstanceRef.current;
    if (!map) return undefined;
    if (!active) {
      removeMarketComparablesLayers(map);
      return undefined;
    }
    const data = {
      radius: buildMarketRadiusGeoJSON(safeCenter, radiusM),
      approx: buildMarketApproxGeoJSON(comparables),
    };
    const upsert = () => {
      try { upsertMarketComparablesLayers(map, data); } catch (_) { /* estilo a medias */ }
    };
    const cancelWait = whenMapStyleReady(map, upsert);
    const onStyleData = () => {
      if (map.isStyleLoaded()) upsert();
    };
    map.on('styledata', onStyleData);
    return () => {
      cancelWait();
      try { map.off('styledata', onStyleData); } catch (_) { /* mapa destruido */ }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- centerKey resume safeCenter.
  }, [mapInstanceRef, active, centerKey, radiusM, comparables]);

  // Encuadre del círculo dejando libre el Dock (al entrar y al cambiar el radio / la unidad).
  useEffect(() => {
    const map = mapInstanceRef.current;
    if (!map || !active) return undefined;
    const bounds = circleBounds(safeCenter, radiusM);
    if (!bounds) return undefined;
    let cancelled = false;
    (async () => {
      await prepareMapForFit(map);
      if (cancelled) return;
      try {
        map.fitBounds(bounds, { padding: clampMapFitPadding(map, fitPaddingRef.current), duration: 700 });
      } catch (_) { /* mapa destruido */ }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- centerKey resume safeCenter.
  }, [mapInstanceRef, active, centerKey, radiusM, fitKey]);

  // Marcador de la unidad.
  useEffect(() => {
    const map = mapInstanceRef.current;
    if (unitMarkerRef.current) {
      try { unitMarkerRef.current.remove(); } catch (_) { /* mapa destruido */ }
      unitMarkerRef.current = null;
    }
    if (!map || !active) return;
    unitMarkerRef.current = new maplibregl.Marker({ element: createMarketUnitMarkerElement(unitLabel), anchor: 'center' })
      .setLngLat([safeCenter.lng, safeCenter.lat])
      .addTo(map);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- centerKey resume safeCenter.
  }, [mapInstanceRef, active, centerKey, unitLabel]);

  // Chips con clúster (layout al terminar cada movimiento, como la lista compartida).
  useEffect(() => {
    const map = mapInstanceRef.current;
    const markers = markersRef.current;
    if (!map || !active) {
      mapHoverKeyRef.current = null;
      hidePopup();
      markers.forEach(({ marker }) => { try { marker.remove(); } catch (_) { /* noop */ } });
      markers.clear();
      return undefined;
    }
    const points = buildComparableMapPoints(comparables, block);
    // Chip más ancho que el de la lista compartida (lleva moneda y unidad): el layout lo usa
    // para abanicos y clústeres; el clúster conserva su tamaño de CSS.
    const chipWidth = marketChipWidth(block?.operation);

    const createElement = (slot) => {
      const el = document.createElement('button');
      el.type = 'button';
      const face = document.createElement('span');
      face.className = 'terrio-record-chip__face';
      el.appendChild(face);
      slot.face = face;
      el.addEventListener('mouseenter', () => {
        if (slot.entry.type === 'cluster') {
          if (!canHover()) return;
          mapHoverClusterKeyRef.current = slot.entry.key;
          mapHoverKeyRef.current = null;
          applyState(slot);
          syncPopup(map);
          return;
        }
        if (slot.entry.type !== 'chip') return;
        if (canHover()) {
          mapHoverKeyRef.current = slot.entry.key;
          mapHoverClusterKeyRef.current = null;
          syncPopup(map);
        }
        callbacksRef.current.onHoverKey?.(slot.entry.key);
      });
      el.addEventListener('mouseleave', () => {
        if (slot.entry.type === 'cluster') {
          if (mapHoverClusterKeyRef.current === slot.entry.key) {
            mapHoverClusterKeyRef.current = null;
            applyState(slot);
            syncPopup(map);
          }
          return;
        }
        if (slot.entry.type !== 'chip') return;
        if (mapHoverKeyRef.current === slot.entry.key) {
          mapHoverKeyRef.current = null;
          // El padre todavía no bajó `hoveredKey = null`: no reabrir este mismo tooltip.
          if (stateRef.current.hoveredKey === slot.entry.key) {
            stateRef.current = { ...stateRef.current, hoveredKey: null };
          }
          syncPopup(map);
        }
        callbacksRef.current.onHoverKey?.(null);
      });
      el.addEventListener('dblclick', (event) => event.stopPropagation());
      el.addEventListener('click', (event) => {
        // El clic no llega al mapa (si no, abriría la parcela de abajo).
        event.stopPropagation();
        const { entry } = slot;
        if (entry.type === 'chip') {
          const already = stateRef.current.selectedKey === entry.key;
          callbacksRef.current.onSelectKey?.(already ? null : entry.key);
          return;
        }
        if (entry.type === 'cluster') {
          const [[minLng, minLat], [maxLng, maxLat]] = entry.bounds;
          if (minLng === maxLng && minLat === maxLat) {
            map.easeTo({ center: entry.lngLat, zoom: Math.min(map.getZoom() + 2, CLUSTER_MAX_ZOOM) });
            return;
          }
          map.fitBounds(entry.bounds, { padding: 96, maxZoom: CLUSTER_MAX_ZOOM, duration: 500 });
        }
      });
      return el;
    };

    const paint = (slot) => {
      const { el, entry } = slot;
      el.classList.toggle('terrio-record-cluster', entry.type === 'cluster');
      el.classList.toggle('terrio-record-chip', entry.type !== 'cluster');
      el.classList.toggle('is-more', entry.type === 'more');
      el.classList.toggle('is-approx', Boolean(entry.type === 'chip' && entry.record?.approx));
      el.classList.toggle('has-tail', Boolean(entry.tail));
      el.style.width = entry.type === 'cluster' ? '' : `${chipWidth}px`;
      if (entry.type === 'cluster') {
        slot.face.textContent = String(entry.count);
        el.setAttribute('aria-label', `${entry.count} comparables, acercar`);
      } else if (entry.type === 'more') {
        slot.face.textContent = `+${entry.count}`;
        el.setAttribute('aria-label', `${entry.count} comparables más`);
      } else {
        slot.face.textContent = entry.record.label;
        el.setAttribute('aria-label', `Comparable ${entry.record.label}`);
      }
      el.dataset.recordKey = entry.key;
      applyState(slot);
    };

    let frame = null;
    const render = () => {
      frame = null;
      const canvas = map.getCanvas?.();
      const entries = layoutRecordChips(points, {
        project: (lngLat) => map.project(lngLat),
        zoom: map.getZoom(),
        viewport: canvas ? { width: canvas.clientWidth, height: canvas.clientHeight } : null,
        chipWidth,
      });
      const next = new Set(entries.map((e) => e.key));
      markers.forEach((slot, key) => {
        if (next.has(key)) return;
        if (popupKeyRef.current === key) hidePopup();
        if (mapHoverKeyRef.current === key) mapHoverKeyRef.current = null;
        if (mapHoverClusterKeyRef.current === key) mapHoverClusterKeyRef.current = null;
        slot.marker.remove();
        markers.delete(key);
      });
      entries.forEach((entry) => {
        const existing = markers.get(entry.key);
        if (existing) {
          existing.entry = entry;
          existing.marker.setLngLat(entry.lngLat);
          existing.marker.setOffset(entry.offset);
          paint(existing);
          return;
        }
        const slot = { entry, el: null, marker: null, face: null };
        slot.el = createElement(slot);
        paint(slot);
        slot.marker = new maplibregl.Marker({
          element: slot.el,
          anchor: entry.type === 'cluster' ? 'center' : 'bottom',
          offset: entry.offset,
        })
          .setLngLat(entry.lngLat)
          .addTo(map);
        markers.set(entry.key, slot);
      });
      // Tooltip del hover o del seleccionado (si su chip quedó en pantalla y no en un clúster).
      syncPopup(map);
    };
    const schedule = () => {
      if (frame != null) return;
      frame = window.requestAnimationFrame(render);
    };
    render();
    map.on('moveend', schedule);
    map.on('resize', schedule);
    return () => {
      if (frame != null) window.cancelAnimationFrame(frame);
      try {
        map.off('moveend', schedule);
        map.off('resize', schedule);
      } catch (_) { /* mapa destruido */ }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- helpers leen refs.
  }, [mapInstanceRef, active, comparables, block?.currency, block?.operation]);

  // Hover / selección: solo clases y tooltip (sin recrear markers).
  useEffect(() => {
    const map = mapInstanceRef.current;
    markersRef.current.forEach((slot) => applyState(slot));
    syncPopup(map);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- applyState / syncPopup leen refs.
  }, [hoveredKey, selectedKey]);

  // Salir del modo / desmontar: markers, tooltip y capas afuera.
  useEffect(() => {
    if (active) return undefined;
    removeAll();
    return undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active]);
  useEffect(() => () => {
    removeAll();
    removeMarketComparablesLayers(mapInstanceRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
}

export default useMarketComparablesLayer;
